<template>
  <div class="input-seat">
    <h2 class="text-h1 text-black font-bold">
      {{ $t("seats", { value }) }}
    </h2>
    <input
      class="input-seat__range"
      type="range"
      :id="id"
      :name="name"
      :min="min"
      :max="max"
      :step="step"
      :value="value"
      :style="{ '--progress': progressPercentage + '%' }"
      @input="onChange"
    />
  </div>
</template>

<script>
import { trackEvent } from "@/services/tracking"

export default {
  name: "InputSeat",
  model: {
    prop: "value",
    event: "change"
  },
  props: {
    id: { type: String, default: null },
    name: { type: String, default: null },
    min: { type: Number, default: 0 },
    max: { type: Number, default: 9999 },
    step: { type: Number, default: 1 },
    value: { type: Number, default: 0 }
  },
  computed: {
    progressPercentage() {
      if (this.max === this.min) return 0
      return ((this.value - this.min) / (this.max - this.min)) * 100
    }
  },
  methods: {
    onChange(event) {
      this.$emit("change", parseInt(event.target.value, 10))
      trackEvent("body-20210217-input_seat", "1-change", event.target.value)
    },
    onPlusClick() {
      if (this.value < this.max) {
        this.$emit("change", this.value + this.step)
      }
      trackEvent("body-20210217-input_seat", "1-click", "plus")
    },
    onMinusClick() {
      if (this.value > this.min) {
        this.$emit("change", this.value - this.step)
        trackEvent("body-20210217-input_seat", "1-change", event.target.value)
      }
      trackEvent("body-20210217-input_seat", "1-click", "minus")
    }
  },
  i18n: {
    messages: {
      en: {
        seats: "{value} seats"
      },
      de: {
        seats: "{value} Plätze"
      },
      es: {
        seats: "{value} usuarios"
      },
      pt: {
        seats: "{value} usuários"
      }
    }
  }
}
</script>

<style lang="scss">
.input-seat {
  $thumb-color: $blue;
  $thumb-size: $spacing-24;
  $track-color: $lightest-grey;
  $track-filled-color: $blue;
  $track-height: $spacing-8;

  display: flex;
  flex-direction: column;
  align-items: center;

  &__top {
    display: flex;
    align-items: center;
    margin-bottom: $spacing-16;
  }

  &__button {
    position: relative;

    flex-shrink: 0;
    width: $spacing-32;
    height: $spacing-32;

    color: $midnight;

    background-color: $lightest-grey;
    border-radius: 50%;
    cursor: pointer;

    svg * {
      fill: currentColor;
    }

    &:disabled {
      cursor: default;
      opacity: 0.5;
    }
  }

  &__value {
    display: block;
    margin: 0 $spacing-16;
  }

  #{&} &__value-input {
    @include font-style(p0);

    min-width: 5em;

    font-weight: bold;
    text-align: center;
  }

  &__range {
    width: 100%;
    margin: $thumb-size / 2 0;

    background-color: transparent;
    border-radius: rem(5px);

    &::-webkit-slider-runnable-track {
      width: 100%;
      height: $track-height;

      background: linear-gradient(
        to right,
        $track-filled-color 0%,
        $track-filled-color var(--progress, 0%),
        $track-color var(--progress, 0%),
        $track-color 100%
      );
      border: 0;
      border-radius: rem(12px);
      cursor: pointer;
    }

    &::-webkit-slider-thumb {
      position: relative;
      top: 50%;

      width: $thumb-size;
      height: $thumb-size;

      background: $thumb-color;
      border: 0;
      border-radius: 50%;
      transform: translateY(-50%);
      cursor: pointer;

      -webkit-appearance: none;
    }

    &:focus::-webkit-slider-runnable-track {
      background: linear-gradient(
        to right,
        $track-filled-color 0%,
        $track-filled-color var(--progress, 0%),
        $track-color var(--progress, 0%),
        $track-color 100%
      );
    }

    &::-moz-range-track {
      width: 100%;
      height: $track-height;

      background: linear-gradient(
        to right,
        $track-filled-color 0%,
        $track-filled-color var(--progress, 0%),
        $track-color var(--progress, 0%),
        $track-color 100%
      );
      border-radius: $track-height;
      cursor: pointer;
    }

    &::-moz-range-thumb {
      width: $thumb-size;
      height: $thumb-size;

      background: $thumb-color;
      border: 0;
      border-radius: 50%;
      cursor: pointer;
    }
  }
}
</style>
